import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import styles from "./FileUpdateForm.module.css";
import i18n from "../../Services/i18n";
import TagsInput from "../TagsInput/TagsInput";
import { MdInsertDriveFile } from "react-icons/md";
import DateTimePicker from "../DateTimePicker/DateTimePicker";
import FileService from "../../Services/fileService";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner"; // <-- spinner
import { addToast } from "../../Services/toastService";

const FileUpdateForm = forwardRef(function FileUpdateForm({ id, onLoaded }, ref) {
  const [formState, setFormState] = useState({
    externalId: null,
    filename: "",
    tags: "",
    Date: null,
    imageUrl: null,
  });
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState("");

  const loadData = async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const data = await FileService.getMetaData(id);

      const d = data?.date ? new Date(data?.date) : null;

      const next = {
        externalId: id,
        filename: data?.filename ?? "",
        tags: typeof data?.tags === "string" ? data.tags : "",
        Date: d,
        imageUrl: data?.thumbnails,
      };

      setFormState(next);
      onLoaded?.(next);
    } catch (e) {
      console.error(e);
      setError(i18n.t("Common.ErrorLoading") || "Erreur lors du chargement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useImperativeHandle(ref, () => ({
    isValid: () => {
      if (!formState.filename || formState.filename.length == 0) {
        addToast(i18n.t("HorseForm.Validation.NameRequired"), "filenamerequired", "ERROR", true);
        return false;
      }
      return true;
    },
    getData: () => ({ ...formState }),
    setData: (patch) => setFormState((prev) => ({ ...prev, ...(patch || {}) })),
    reload: () => loadData(),
  }));

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFormState((s) => ({ ...s, [name]: value }));
  };

  const handleTagsChange = (value) => {
    setFormState((s) => ({
      ...s,
      tags: typeof value === "string" ? value : "",
    }));
  };

  const handleDateChange = (date) => {
    setFormState((s) => ({ ...s, Date: date }));
  };

  return (
    <div className={styles.formShell}>
      <div className={styles.grid}>
        <aside className={styles.thumbCol}>
          <div className={styles.thumbBox}>
            {formState.imageUrl && formState.imageUrl !== null && formState.imageUrl !== "null" ? (
              <img
                src={formState.imageUrl}
                alt={formState.filename || "thumbnail"}
                className={styles.thumbImg}
                draggable={false}
              />
            ) : (
              <MdInsertDriveFile className={styles.fileIcon} />
            )}
          </div>
        </aside>

        <section className={styles.formCol}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.row}>
            <div className={styles.col}>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="filename">
                  {i18n.t("FileUploaderModal.FieldFileName")}
                </label>
                <input
                  type="text"
                  id="filename"
                  name="filename"
                  value={formState.filename}
                  onChange={handleTextChange}
                  className={styles.input}
                  placeholder={i18n.t("FileUploaderModal.FieldFileName")}
                  disabled={loading}
                />
                <div className={styles.hint}>{i18n.t("FileUploaderModal.FileNameHint") ?? ""}</div>
              </div>
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.col}>
              <label className={styles.label}>{i18n.t("FileUploaderModal.FieldTags")}</label>
              <div className={styles.tagsWrapper} onKeyDown={(e) => e.stopPropagation()}>
                <TagsInput value={formState.tags} onChange={handleTagsChange} disabled={loading} />
              </div>
              <div className={styles.hint}>{i18n.t("FileUploaderModal.TagsHint") ?? ""}</div>
            </div>
          </div>

          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>
              {i18n.t("FileUploaderModal.FieldChooseADate")}
            </legend>
            <DateTimePicker
              name="birthdate"
              value={formState.Date}
              onChange={handleDateChange}
              readOnly={false}
            />
          </fieldset>
        </section>

        {loading && (
          <div className={styles.overlay}>
            <LoadingSpinner size={40} />
          </div>
        )}
      </div>
    </div>
  );
});

export default FileUpdateForm;
